import { Filter as FilterIcon } from 'lucide-react';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button, IconButton } from './components/Button';
import { Chip } from './components/Chip';
import { FilterChip, SortingChip } from './components/DraggableFilterItem';
import { Drawer } from './components/Drawer';
import { Flex } from './components/Flex';
import { Input } from './components/Input';
import { Select } from './components/Select';
import { useMediaQuery } from './hooks/useMediaQuery';
import { UserContext } from './UserContext';
import { getDefaultConfigForRoute } from './utils/defaultFilterConfigs';
import {
  operators,
  possibleFilters,
  possibleSortings,
} from './utils/filterConstants';
import { applySortingAndFilter } from './utils/filterLogic';
import {
  clearFromLocalStorage,
  decodeFromURL,
  encodeToURL,
  loadFromLocalStorage,
  mergeFilterStates,
  saveToLocalStorage,
} from './utils/filterStateManager';
import { getFieldType, validateFilterValue } from './utils/validation';

export function FilterAndSorting({ onApply, data, loading = false }) {
  const user_data = useContext(UserContext);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width:600px)');

  // Get default config for current route
  const defaultConfig = getDefaultConfigForRoute(location.pathname);

  const [showControls, setShowControls] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [appliedSorting, setAppliedSorting] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('title');
  const [selectedOperator, setSelectedOperator] = useState('contains');
  const [filterValue, setFilterValue] = useState('');
  const [filterError, setFilterError] = useState('');

  // Initialize state from URL or localStorage on mount/route change
  useEffect(() => {
    // Priority: URL params > localStorage > defaults
    const urlState = decodeFromURL(searchParams);
    const savedState = loadFromLocalStorage(location.pathname);

    const state = mergeFilterStates(defaultConfig, urlState || savedState);

    setAppliedFilters(state.filters);
    setAppliedSorting(state.sorting);
  }, [location.pathname]);

  function updateSelectedFilter(filterId) {
    setSelectedFilter(filterId);
    setSelectedOperator(possibleFilters[filterId].operators[0]);
    setFilterError('');
  }

  function apply() {
    const filteredAndSorted = applySortingAndFilter(
      data,
      appliedFilters,
      appliedSorting,
      user_data?.id,
    );
    onApply(filteredAndSorted);
  }

  // Save state to localStorage and update URL whenever filters/sorting change
  useEffect(() => {
    if (appliedFilters.length > 0 || appliedSorting.length > 0) {
      const state = { filters: appliedFilters, sorting: appliedSorting };
      saveToLocalStorage(location.pathname, state);

      // Update URL with encoded state
      const params = encodeToURL(state);
      setSearchParams(params, { replace: true });
    }
  }, [appliedFilters, appliedSorting, location.pathname, setSearchParams]);

  function getIndex(arr, itemToCompare) {
    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      let match = true;
      Object.keys(itemToCompare).forEach((key) => {
        match = match && item[key] == itemToCompare[key];
      });
      if (match) {
        return i;
      }
    }
  }

  function addFilter() {
    const validation = validateFilterValue(
      selectedFilter,
      selectedOperator,
      filterValue,
    );

    if (!validation.isValid) {
      setFilterError(validation.message || 'Invalid value');
      return;
    }

    setFilterError('');
    const filter = {
      id: selectedFilter,
      operator: selectedOperator,
      value: filterValue,
      enabled: true,
    };
    setAppliedFilters([...appliedFilters, filter]);
    setFilterValue('');
  }

  function toggleFilter(item) {
    var data = toggleItem(appliedFilters, item);
    setAppliedFilters(data);
  }

  function toggleItem(arr, item) {
    var index = getIndex(arr, item);
    const newItem = { ...item, enabled: !item.enabled };
    var data = [...arr];
    data.splice(index, 1, newItem);
    return data;
  }

  function deleteFilter(item) {
    var data = deleteItem(appliedFilters, item);
    setAppliedFilters(data);
  }

  function deleteItem(arr, item) {
    var index = getIndex(arr, item);
    var data = [...arr];
    data.splice(index, 1);
    return data;
  }

  function addSorting(id) {
    // Check if sorting already exists
    const exists = appliedSorting.some((s) => s.id === id);
    if (exists) return;

    const sorting = {
      id: id,
      direction: 'desc',
      enabled: true,
    };
    setAppliedSorting([...appliedSorting, sorting]);
  }

  function toggleSorting(item) {
    const index = appliedSorting.findIndex(
      (s) => s.id === item.id && s.direction === item.direction,
    );
    if (index >= 0) {
      const newItem = { ...item, enabled: !item.enabled };
      var data = [...appliedSorting];
      data.splice(index, 1, newItem);
      setAppliedSorting(data);
    }
  }

  function toggleSortingDirection(item) {
    const direction = item.direction == 'asc' ? 'desc' : 'asc';
    const index = appliedSorting.findIndex(
      (s) => s.id === item.id && s.direction === item.direction,
    );
    if (index >= 0) {
      const newItem = { ...item, direction };
      var data = [...appliedSorting];
      data.splice(index, 1, newItem);
      setAppliedSorting(data);
    }
  }

  function deleteSorting(item) {
    var data = deleteItem(appliedSorting, item);
    setAppliedSorting(data);
  }

  function resetToDefaults() {
    setAppliedFilters(defaultConfig.filters);
    setAppliedSorting(defaultConfig.sorting);
    clearFromLocalStorage(location.pathname);
    setSearchParams({}, { replace: true });
    setFilterError('');
  }

  // Helper to swap array elements
  const arrayMove = (arr, from, to) => {
    const newArr = [...arr];
    const item = newArr[from];
    newArr.splice(from, 1);
    newArr.splice(to, 0, item);
    return newArr;
  };

  // Reordering handlers
  function handleMoveSortingUp(item) {
    const index = appliedSorting.findIndex(
      (s) => s.id === item.id && s.direction === item.direction,
    );
    if (index > 0) {
      const newSorting = arrayMove(appliedSorting, index, index - 1);
      setAppliedSorting(newSorting);
    }
  }

  function handleMoveSortingDown(item) {
    const index = appliedSorting.findIndex(
      (s) => s.id === item.id && s.direction === item.direction,
    );
    if (index < appliedSorting.length - 1) {
      const newSorting = arrayMove(appliedSorting, index, index + 1);
      setAppliedSorting(newSorting);
    }
  }

  function handleMoveFilterUp(item) {
    const index = appliedFilters.findIndex(
      (f) =>
        f.id === item.id &&
        f.operator === item.operator &&
        f.value === item.value,
    );
    if (index > 0) {
      const newFilters = arrayMove(appliedFilters, index, index - 1);
      setAppliedFilters(newFilters);
    }
  }

  function handleMoveFilterDown(item) {
    const index = appliedFilters.findIndex(
      (f) =>
        f.id === item.id &&
        f.operator === item.operator &&
        f.value === item.value,
    );
    if (index < appliedFilters.length - 1) {
      const newFilters = arrayMove(appliedFilters, index, index + 1);
      setAppliedFilters(newFilters);
    }
  }

  useEffect(() => {
    // While loading with no data, keep skeletons and avoid extra work
    if (loading && data.length === 0) {
      return;
    }

    // No data after load -> empty state
    if (data.length === 0) {
      onApply([]);
      return;
    }

    apply();
  }, [appliedFilters, appliedSorting, data]);

  return (
    <div className="filterAndSortingBox">
      <Flex
        direction="row"
        align="center"
        gap="0.75rem"
        style={{ alignItems: 'flex-start' }}
        className="filterAndSortingBox__header"
      >
        <IconButton
          onClick={() => setShowControls(!showControls)}
          title="Toggle filter controls"
          aria-label="Toggle filter controls"
        >
          <FilterIcon size={18} />
        </IconButton>

        <div className="filterAndSortingBox__summary">
          {/* Applied Sortings */}
          {appliedSorting.length > 0 && (
            <div className="filterAndSortingBox__section">
              <div className="filterAndSortingBox__section-title">
                Sorting ({appliedSorting.filter((s) => s.enabled).length})
              </div>
              <div className="filterAndSortingBox__section-items">
                {appliedSorting.map((item, i) => (
                  <SortingChip
                    key={i}
                    item={item}
                    label={possibleSortings[item.id].label}
                    isMobile={isMobile}
                    onToggle={() => toggleSorting(item)}
                    onToggleDirection={() => toggleSortingDirection(item)}
                    onDelete={() => deleteSorting(item)}
                    onMoveUp={handleMoveSortingUp}
                    onMoveDown={handleMoveSortingDown}
                    canMoveUp={
                      appliedSorting.findIndex(
                        (s) =>
                          s.id === item.id && s.direction === item.direction,
                      ) > 0
                    }
                    canMoveDown={
                      appliedSorting.findIndex(
                        (s) =>
                          s.id === item.id && s.direction === item.direction,
                      ) <
                      appliedSorting.length - 1
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Applied Filters */}
          {appliedFilters.length > 0 && (
            <div className="filterAndSortingBox__section">
              <div className="filterAndSortingBox__section-title">
                Filters ({appliedFilters.filter((f) => f.enabled).length})
              </div>
              <div className="filterAndSortingBox__section-items">
                {appliedFilters.map((item, i) => {
                  const canMoveUp =
                    appliedFilters.findIndex(
                      (f) =>
                        f.id === item.id &&
                        f.operator === item.operator &&
                        f.value === item.value,
                    ) > 0;
                  const canMoveDown =
                    appliedFilters.findIndex(
                      (f) =>
                        f.id === item.id &&
                        f.operator === item.operator &&
                        f.value === item.value,
                    ) <
                    appliedFilters.length - 1;

                  return (
                    <FilterChip
                      key={i}
                      item={item}
                      label={`${possibleFilters[item.id].label} ${item.operator} ${item.value}`}
                      isMobile={isMobile}
                      onDelete={() => deleteFilter(item)}
                      onToggle={() => toggleFilter(item)}
                      onMoveUp={handleMoveFilterUp}
                      onMoveDown={handleMoveFilterDown}
                      canMoveUp={canMoveUp}
                      canMoveDown={canMoveDown}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state */}
          {appliedSorting.length === 0 && appliedFilters.length === 0 && (
            <div className="filterAndSortingBox__empty">
              No filters or sorting applied
            </div>
          )}
        </div>
      </Flex>

      {/* Filter Controls Drawer - slides from left */}
      <Drawer
        open={showControls}
        anchor="left"
        width={340}
        onClose={() => setShowControls(false)}
      >
        <div
          style={{
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            height: '100%',
            overflowY: 'auto',
          }}
        >
          {/* Add Filter Section */}
          <div className="filterAndSortingBox__section">
            <div className="filterAndSortingBox__section-title">Add Filter</div>
            <div className="filterAndSortingBox__filters">
              <div className="filterAndSortingBox__filters-row">
                <Select
                  label="Field"
                  value={selectedFilter}
                  onChange={(e) => updateSelectedFilter(e.target.value)}
                  options={Object.keys(possibleFilters).map((filterId) => ({
                    value: filterId,
                    label: possibleFilters[filterId].label,
                  }))}
                />
                <Select
                  label="Operator"
                  value={selectedOperator}
                  onChange={(e) => setSelectedOperator(e.target.value)}
                  options={possibleFilters[selectedFilter].operators.map(
                    (op) => ({
                      value: op,
                      label: operators[op],
                    }),
                  )}
                />
              </div>
              <div className="filterAndSortingBox__filters-row">
                <Input
                  label="Value"
                  required
                  value={filterValue}
                  onChange={(e) => {
                    setFilterValue(e.target.value);
                    setFilterError('');
                  }}
                  error={!!filterError}
                  errorMessage={filterError}
                  helperText={`Expected: ${getFieldType(selectedFilter)}`}
                  placeholder={`Enter ${getFieldType(selectedFilter)}`}
                />
                <Button
                  variant="primary"
                  disabled={!filterValue}
                  onClick={addFilter}
                  style={{ alignSelf: 'flex-end', marginBottom: '0.35rem' }}
                >
                  Add Filter
                </Button>
              </div>
            </div>
          </div>

          {/* Add Sorting Section */}
          <div className="filterAndSortingBox__section">
            <div className="filterAndSortingBox__section-title">
              Add Sorting
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {Object.keys(possibleSortings).map((sortingId, i) => (
                <Chip
                  key={i}
                  label={possibleSortings[sortingId].label}
                  onClick={() => addSorting(sortingId)}
                />
              ))}
            </div>
          </div>

          {/* Reset Button */}
          <Button
            variant="secondary"
            onClick={resetToDefaults}
            style={{ alignSelf: 'flex-start' }}
          >
            Reset to Default
          </Button>
        </div>
      </Drawer>
    </div>
  );
}
